import React, { useEffect, useState, useCallback } from 'react';
import { api, formatApiError } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { Plus, Trash2, KeyRound, CreditCard, Mail, MessageSquare, Activity, Settings as SettingsIcon, Users as UsersIcon, Briefcase, ShieldCheck } from 'lucide-react';

const TABS = [
  { id: 'users',         label: 'Staff & permissions', Icon: UsersIcon },
  { id: 'business-lines',label: 'Business lines',      Icon: Briefcase },
  { id: 'modules',       label: 'Modules',             Icon: SettingsIcon },
  { id: 'integrations',  label: 'Integrations',        Icon: ShieldCheck },
  { id: 'audit',         label: 'Audit log',           Icon: Activity },
  { id: 'account',       label: 'My account',          Icon: KeyRound },
];

const Settings = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState('users');

  if (user?.role !== 'super_admin') {
    return <div className="empty-state" data-testid="settings-denied"><div className="ttl">Settings is super-admin only</div><div className="sub">Ask your super admin for access.</div></div>;
  }

  return (
    <>
      <div className="page-toolbar" data-testid="settings-toolbar">
        <span className="page-title">Settings</span>
      </div>
      <div className="settings-tabs">
        {TABS.map((t) => (
          <div key={t.id} className={`settings-tab${tab === t.id ? ' active' : ''}`} onClick={() => setTab(t.id)} data-testid={`settings-tab-${t.id}`}>
            <t.Icon size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />{t.label}
          </div>
        ))}
      </div>
      <div className="settings-content">
        {tab === 'users' && <UsersTab />}
        {tab === 'business-lines' && <LobsTab />}
        {tab === 'modules' && <ModulesTab />}
        {tab === 'integrations' && <IntegrationsTab />}
        {tab === 'audit' && <AuditTab />}
        {tab === 'account' && <AccountTab />}
      </div>
    </>
  );
};

// ── USERS TAB ──────────────────────────────────────────────────────────────────
const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [modules, setModules] = useState([]);
  const { lobs } = useData();
  const [err, setErr] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const { user: me } = useAuth();

  const load = useCallback(() => {
    Promise.all([api.get('/users'), api.get('/users/modules')])
      .then(([u, m]) => { setUsers(u.data); setModules(m.data.modules); })
      .catch((e) => setErr(formatApiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (u) => {
    if (!window.confirm(`Delete ${u.name} (${u.email})?`)) return;
    try { await api.delete(`/users/${u.id}`); load(); } catch (e) { alert(formatApiError(e)); }
  };

  return (
    <div data-testid="users-tab">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 12, color: 'var(--text-faded)' }}>{users.length} user{users.length !== 1 ? 's' : ''}</div>
        <button className="btn-primary" onClick={() => setShowCreate(true)} data-testid="add-user-btn"><Plus size={11} style={{ marginRight: 4 }} />Add staff member</button>
      </div>
      {err && <div className="f-error">{err}</div>}
      <div className="card">
        <table className="tbl">
          <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Modules</th><th>Business lines</th><th>Active</th><th></th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td>{u.name}</td>
                <td>{u.email}{u.id === me?.id && <span className="tag-pill ok" style={{ marginLeft: 6 }}>You</span>}</td>
                <td>{u.role === 'super_admin' ? <span className="tag-pill warn">Super admin</span> : <span className="tag-pill muted">Staff</span>}</td>
                <td>{u.role === 'super_admin' ? 'All' : ((u.allowed_modules || []).join(', ') || '—')}</td>
                <td>{u.role === 'super_admin' ? 'All' : (u.allowed_lobs || []).map((id) => lobs.find((l) => l.id === id)?.label || id).join(', ') || '—'}</td>
                <td><span className={`tag-pill ${u.active ? 'ok' : 'muted'}`}>{u.active ? 'Yes' : 'No'}</span></td>
                <td className="tbl-actions">
                  <button className="btn-link" onClick={() => setEditing(u)} data-testid={`edit-user-${u.id}`}>Edit</button>
                  <button className="btn-link" style={{ color: 'var(--error)' }} onClick={() => del(u)} data-testid={`del-user-${u.id}`} disabled={u.id === me?.id}><Trash2 size={11} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(showCreate || editing) && <UserModal modules={modules} lobs={lobs} user={editing} onClose={(saved) => { setShowCreate(false); setEditing(null); if (saved) load(); }} />}
    </div>
  );
};

const UserModal = ({ modules, lobs, user, onClose }) => {
  const [form, setForm] = useState(user || { name: '', email: '', password: '', role: 'staff', active: true, allowed_modules: ['dashboard', 'contacts'], allowed_lobs: [] });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleArr = (k, val) => setForm((f) => { const cur = new Set(f[k] || []); cur.has(val) ? cur.delete(val) : cur.add(val); return { ...f, [k]: Array.from(cur) }; });

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr('');
    try {
      if (user) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api.patch(`/users/${user.id}`, payload);
      } else { await api.post('/users', form); }
      onClose(true);
    } catch (ex) { setErr(formatApiError(ex)); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay-v2" onClick={() => onClose(false)}>
      <form className="modal-card wide" onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="user-modal">
        <div className="modal-title">{user ? `Edit ${user.email}` : 'Add staff member'}</div>
        <div className="modal-row">
          <div><label className="f-label">Full name</label><input className="f-input" required value={form.name} onChange={(e) => set('name', e.target.value)} data-testid="um-name" /></div>
          <div><label className="f-label">Email</label><input className="f-input" type="email" required value={form.email} onChange={(e) => set('email', e.target.value)} disabled={!!user} data-testid="um-email" /></div>
        </div>
        <div className="modal-row">
          <div><label className="f-label">Role</label><select className="f-select" value={form.role} onChange={(e) => set('role', e.target.value)} data-testid="um-role"><option value="staff">Staff</option><option value="super_admin">Super admin</option></select></div>
          <div><label className="f-label">{user ? 'New password (leave blank)' : 'Password (min 8)'}</label><input className="f-input" type="password" value={form.password || ''} onChange={(e) => set('password', e.target.value)} minLength={user ? 0 : 8} required={!user} data-testid="um-password" /></div>
        </div>
        <label className="f-checkbox"><input type="checkbox" checked={form.active} onChange={() => set('active', !form.active)} data-testid="um-active" />Active</label>

        {form.role !== 'super_admin' && (
          <>
            <div style={{ fontSize: 12, fontWeight: 600, marginTop: 14, marginBottom: 4 }}>Modules</div>
            <div className="perm-grid">
              {modules.map((m) => (
                <label className="f-checkbox" key={m}>
                  <input type="checkbox" checked={form.allowed_modules.includes(m)} onChange={() => toggleArr('allowed_modules', m)} data-testid={`um-mod-${m}`} />
                  {m.charAt(0).toUpperCase() + m.slice(1)}
                </label>
              ))}
            </div>
            <div style={{ fontSize: 12, fontWeight: 600, marginTop: 14, marginBottom: 4 }}>Business lines</div>
            <div className="perm-grid">
              {lobs.map((l) => (
                <label className="f-checkbox" key={l.id}>
                  <input type="checkbox" checked={form.allowed_lobs.includes(l.id)} onChange={() => toggleArr('allowed_lobs', l.id)} data-testid={`um-lob-${l.key}`} />
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: l.accent, display: 'inline-block', marginRight: 4 }} />
                  {l.label}
                </label>
              ))}
            </div>
          </>
        )}

        {err && <div className="f-error">{err}</div>}
        <div className="modal-actions">
          <button type="button" className="btn-secondary" onClick={() => onClose(false)}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={busy} data-testid="um-submit">{busy ? 'Saving…' : (user ? 'Save' : 'Create')}</button>
        </div>
      </form>
    </div>
  );
};

// ── LOBS TAB ──────────────────────────────────────────────────────────────────
const LobsTab = () => {
  const { lobs, reloadLobs } = useData();
  const [editing, setEditing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const del = async (l) => {
    if (!window.confirm(`Delete business line "${l.label}"?`)) return;
    try { await api.delete(`/lobs/${l.id}`); reloadLobs(true); } catch (e) { alert(formatApiError(e)); }
  };

  const toggleActive = async (l) => {
    try { await api.patch(`/lobs/${l.id}`, { is_active: l.is_active === false }); reloadLobs(true); }
    catch (e) { alert(formatApiError(e)); }
  };

  return (
    <div data-testid="lobs-tab">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 12, color: 'var(--text-faded)' }}>{lobs.length} business line{lobs.length !== 1 ? 's' : ''}</div>
        <button className="btn-primary" onClick={() => setShowCreate(true)} data-testid="add-lob-btn"><Plus size={11} style={{ marginRight: 4 }} />Add business line</button>
      </div>
      <div className="card">
        <table className="tbl">
          <thead><tr><th>Label</th><th>Key</th><th>Stages</th><th>Deal types</th><th>Visible</th><th></th></tr></thead>
          <tbody>
            {lobs.map((l) => (
              <tr key={l.id} data-testid={`lob-row-${l.key}`} style={l.is_active === false ? { opacity: 0.55 } : undefined}>
                <td><span style={{ width: 10, height: 10, background: l.accent, borderRadius: '50%', display: 'inline-block', marginRight: 6 }} />{l.icon} {l.label}</td>
                <td><code>{l.key}</code></td>
                <td>{(l.pipelineStages || []).length}</td>
                <td>{(l.dealTypes || []).join(', ') || '—'}</td>
                <td>
                  <label className="f-checkbox" style={{ margin: 0 }}>
                    <input type="checkbox" checked={l.is_active !== false} onChange={() => toggleActive(l)} data-testid={`lob-active-toggle-${l.key}`} />
                    {l.is_active === false ? 'Hidden' : 'Shown'}
                  </label>
                </td>
                <td className="tbl-actions">
                  <button className="btn-link" onClick={() => setEditing(l)} data-testid={`edit-lob-${l.key}`}>Edit</button>
                  <button className="btn-link" style={{ color: 'var(--error)' }} onClick={() => del(l)} data-testid={`del-lob-${l.key}`}><Trash2 size={11} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(showCreate || editing) && <LobModal lob={editing} onClose={(saved) => { setShowCreate(false); setEditing(null); if (saved) reloadLobs(true); }} />}
    </div>
  );
};

const LobModal = ({ lob, onClose }) => {
  const [form, setForm] = useState(lob ? {
    ...lob,
    pipeline_stages: lob.pipelineStages || lob.pipeline_stages || [],
    deal_types: lob.dealTypes || lob.deal_types || [],
  } : {
    key: '', label: '', icon: '💼',
    accent: '#3368b8', accent_bg: '#eaf1fa', accent_tc: '#26456f', accent_bg_dark: '#1c2c44', accent_tc_dark: '#aac4ec',
    pipeline_stages: [{ key: 'intake', label: 'Intake', prob: 10 }, { key: 'won', label: 'Won', prob: 100 }],
    deal_types: [],
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setStage = (i, k, v) => setForm((f) => ({ ...f, pipeline_stages: f.pipeline_stages.map((s, idx) => idx === i ? { ...s, [k]: k === 'prob' ? Number(v) || 0 : v } : s) }));
  const addStage = () => set('pipeline_stages', [...form.pipeline_stages, { key: '', label: '', prob: 0 }]);
  const removeStage = (i) => set('pipeline_stages', form.pipeline_stages.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr('');
    const payload = { ...form, deal_types: typeof form.deal_types === 'string' ? form.deal_types.split(',').map((s) => s.trim()).filter(Boolean) : form.deal_types };
    try {
      if (lob) { const { key, ...rest } = payload; await api.patch(`/lobs/${lob.id}`, rest); }
      else { await api.post('/lobs', payload); }
      onClose(true);
    } catch (ex) { setErr(formatApiError(ex)); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay-v2" onClick={() => onClose(false)}>
      <form className="modal-card wide" onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="lob-modal">
        <div className="modal-title">{lob ? `Edit ${lob.label}` : 'Add business line'}</div>
        <div className="modal-row">
          <div><label className="f-label">Label</label><input className="f-input" required value={form.label} onChange={(e) => set('label', e.target.value)} data-testid="lobm-label" /></div>
          <div><label className="f-label">Key (no spaces)</label><input className="f-input" required value={form.key} onChange={(e) => set('key', e.target.value.toLowerCase().replace(/\s+/g, ''))} disabled={!!lob} data-testid="lobm-key" /></div>
          <div><label className="f-label">Icon</label><input className="f-input" value={form.icon} onChange={(e) => set('icon', e.target.value)} /></div>
        </div>
        <div className="modal-row">
          <div><label className="f-label">Accent (hex)</label><input className="f-input" value={form.accent} onChange={(e) => set('accent', e.target.value)} /></div>
          <div><label className="f-label">BG (light)</label><input className="f-input" value={form.accent_bg} onChange={(e) => set('accent_bg', e.target.value)} /></div>
          <div><label className="f-label">Text (light)</label><input className="f-input" value={form.accent_tc} onChange={(e) => set('accent_tc', e.target.value)} /></div>
        </div>
        <div className="modal-row">
          <div><label className="f-label">BG (dark)</label><input className="f-input" value={form.accent_bg_dark} onChange={(e) => set('accent_bg_dark', e.target.value)} /></div>
          <div><label className="f-label">Text (dark)</label><input className="f-input" value={form.accent_tc_dark} onChange={(e) => set('accent_tc_dark', e.target.value)} /></div>
          <div><label className="f-label">Deal types (comma)</label><input className="f-input" value={Array.isArray(form.deal_types) ? form.deal_types.join(', ') : form.deal_types} onChange={(e) => set('deal_types', e.target.value)} /></div>
        </div>
        <div style={{ fontSize: 12, fontWeight: 600, marginTop: 14, marginBottom: 6 }}>Pipeline stages</div>
        {form.pipeline_stages.map((s, i) => (
          <div key={i} className="modal-row" style={{ marginBottom: 6 }}>
            <div><input className="f-input" placeholder="Key" value={s.key} onChange={(e) => setStage(i, 'key', e.target.value)} required /></div>
            <div><input className="f-input" placeholder="Label" value={s.label} onChange={(e) => setStage(i, 'label', e.target.value)} required /></div>
            <div><input className="f-input" type="number" min="0" max="100" placeholder="% prob" value={s.prob} onChange={(e) => setStage(i, 'prob', e.target.value)} /></div>
            <button type="button" className="btn-link" style={{ color: 'var(--error)' }} onClick={() => removeStage(i)}><Trash2 size={11} /></button>
          </div>
        ))}
        <button type="button" className="btn-link" onClick={addStage}><Plus size={11} /> Add stage</button>

        {err && <div className="f-error">{err}</div>}
        <div className="modal-actions">
          <button type="button" className="btn-secondary" onClick={() => onClose(false)}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={busy} data-testid="lobm-submit">{busy ? 'Saving…' : (lob ? 'Save' : 'Create')}</button>
        </div>
      </form>
    </div>
  );
};

// ── MODULES TAB ────────────────────────────────────────────────────────────────
const ModulesTab = () => {
  const [available, setAvailable] = useState([]);
  const [enabled, setEnabled] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get('/settings/app').then(({ data }) => {
      setAvailable(data.available_modules);
      setEnabled(data.enabled_modules);
    }).catch((e) => setErr(formatApiError(e)));
  }, []);

  const toggle = (m) => {
    if (m === 'settings') return; // cannot disable
    setEnabled((en) => en.includes(m) ? en.filter((x) => x !== m) : [...en, m]);
  };

  const save = async () => {
    setBusy(true); setMsg(''); setErr('');
    try {
      await api.put('/settings/modules', { enabled_modules: enabled });
      setMsg('Module visibility updated. Active staff will see changes on next page load.');
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="modules-tab">
      <div className="feature-card">
        <div className="ttl"><SettingsIcon size={12} /> Global module visibility</div>
        <div className="sub">Disabled modules disappear from the topbar for everyone, including super admins. Settings cannot be disabled.</div>
        <div className="perm-grid" style={{ marginTop: 12 }}>
          {available.map((m) => (
            <label key={m} className="f-checkbox">
              <input type="checkbox" checked={enabled.includes(m)} onChange={() => toggle(m)} disabled={m === 'settings'} data-testid={`module-toggle-${m}`} />
              {m.charAt(0).toUpperCase() + m.slice(1)}
              {m === 'settings' && <span className="tag-pill muted" style={{ marginLeft: 6 }}>locked</span>}
            </label>
          ))}
        </div>
        {msg && <div className="module-banner" style={{ marginTop: 10 }}>{msg}</div>}
        {err && <div className="f-error">{err}</div>}
        <div style={{ marginTop: 12 }}>
          <button className="btn-primary" onClick={save} disabled={busy} data-testid="modules-save">{busy ? 'Saving…' : 'Save module visibility'}</button>
        </div>
      </div>
    </div>
  );
};

// ── INTEGRATIONS TAB ───────────────────────────────────────────────────────────
const IntegrationsTab = () => {
  const [stripe, setStripe] = useState(null);
  const [email, setEmail] = useState(null);
  const [twilio, setTwilio] = useState(null);
  const [emailProvider, setEmailProvider] = useState('none');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    Promise.all([
      api.get('/settings/integrations/stripe').then((r) => r.data),
      api.get('/settings/integrations/email').then((r) => r.data),
      api.get('/settings/integrations/twilio').then((r) => r.data),
    ]).then(([s, e, t]) => { setStripe(s); setEmail(e); setTwilio(t); setEmailProvider(e.provider); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveEmail = async () => {
    setBusy(true); setMsg('');
    try { await api.put('/settings/integrations/email', { provider: emailProvider }); setMsg('Email provider preference saved.'); load(); }
    catch (ex) { setMsg('Error: ' + formatApiError(ex)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="integrations-tab">
      <div className="feature-card" data-testid="stripe-card">
        <div className="ttl"><CreditCard size={12} /> Stripe payments
          {stripe?.mode === 'live' && <span className="tag-pill ok">Live</span>}
          {stripe?.mode === 'test' && <span className="tag-pill warn">Test</span>}
          {stripe?.mode === 'dev_proxy' && <span className="tag-pill warn">Dev proxy</span>}
          {!stripe?.configured && <span className="tag-pill err">Not configured</span>}
        </div>
        <div className="sub">Powers checkout links, product sales, subscriptions, refunds, and one-click upsells.</div>
        <div className="grid">
          <div className="field-row"><b>Mode</b><div>{stripe?.mode || '—'}</div></div>
          <div className="field-row"><b>API key</b><div>{stripe?.key_prefix || '—'}</div></div>
          <div className="field-row"><b>Webhook URL</b><div><code style={{ fontSize: 10 }}>{stripe?.webhook_url}</code></div></div>
          <div className="field-row">
            <b>Capabilities</b>
            <div style={{ fontSize: 11 }}>
              One-time: <span className={`tag-pill ${stripe?.configured ? 'ok' : 'muted'}`}>{stripe?.configured ? 'yes' : 'no'}</span>{' '}
              Subscriptions: <span className={`tag-pill ${stripe?.supports_subscriptions ? 'ok' : 'muted'}`}>{stripe?.supports_subscriptions ? 'yes' : 'no'}</span>{' '}
              Refunds: <span className={`tag-pill ${stripe?.supports_refunds ? 'ok' : 'muted'}`}>{stripe?.supports_refunds ? 'yes' : 'no'}</span>{' '}
              Upsells: <span className={`tag-pill ${stripe?.supports_upsells ? 'ok' : 'muted'}`}>{stripe?.supports_upsells ? 'yes' : 'no'}</span>
            </div>
          </div>
        </div>
        {stripe?.mode === 'dev_proxy' && (
          <div className="module-banner" style={{ marginTop: 10 }}>
            The dev key <code>sk_test_emergent</code> is shared and only supports one-time payments. For subscriptions / refunds / one-click upsells, paste your own <code>sk_test_…</code> or <code>sk_live_…</code> key into <code>backend/.env</code> and restart the backend.
          </div>
        )}
      </div>

      <div className="feature-card" data-testid="email-card">
        <div className="ttl"><Mail size={12} /> Email provider {email?.configured ? <span className="tag-pill ok">Configured</span> : <span className="tag-pill warn">Records only</span>}</div>
        <div className="sub">{email?.note}</div>
        <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
          <select className="f-select" value={emailProvider} onChange={(e) => setEmailProvider(e.target.value)} style={{ maxWidth: 220 }} data-testid="email-provider-select">
            <option value="none">Records only (no send)</option>
            <option value="resend">Resend</option>
            <option value="sendgrid">SendGrid</option>
            <option value="smtp">Custom SMTP</option>
          </select>
          <button className="btn-primary" onClick={saveEmail} disabled={busy} data-testid="email-save">{busy ? 'Saving…' : 'Save'}</button>
        </div>
        {msg && <div className="module-banner" style={{ marginTop: 8 }}>{msg}</div>}
        <div style={{ marginTop: 10, fontSize: 11, color: 'var(--text-faded)' }}>
          Picking a provider stores the preference. Adding the actual API key + the send wiring is a follow-up task.
        </div>
      </div>

      <div className="feature-card" data-testid="twilio-card">
        <div className="ttl"><MessageSquare size={12} /> Twilio SMS {twilio?.configured ? <span className="tag-pill ok">Configured</span> : <span className="tag-pill warn">Not configured</span>}</div>
        <div className="sub">{twilio?.note}</div>
        <div className="grid">
          <div className="field-row"><b>Account SID</b><div>{twilio?.account_sid_prefix || '—'}</div></div>
          <div className="field-row"><b>From number</b><div>{twilio?.from_number || '—'}</div></div>
        </div>
      </div>
    </div>
  );
};

// ── AUDIT TAB ──────────────────────────────────────────────────────────────────
const AuditTab = () => {
  const [rows, setRows] = useState([]);
  const [actionFilter, setActionFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  const [err, setErr] = useState('');

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (actionFilter) params.set('action', actionFilter);
    if (emailFilter) params.set('actor_email', emailFilter);
    api.get(`/audit?${params.toString()}`).then((r) => setRows(r.data)).catch((e) => setErr(formatApiError(e)));
  }, [actionFilter, emailFilter]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div data-testid="audit-tab">
      <div className="feature-card">
        <div className="ttl"><Activity size={12} /> Audit log</div>
        <div className="sub">Every meaningful staff action is recorded here. Use this for compliance reviews and support.</div>
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <input className="f-input" placeholder="Filter by action (e.g. user.create)" value={actionFilter} onChange={(e) => setActionFilter(e.target.value)} style={{ maxWidth: 260 }} data-testid="audit-action-filter" />
          <input className="f-input" placeholder="Filter by actor email" value={emailFilter} onChange={(e) => setEmailFilter(e.target.value)} style={{ maxWidth: 260 }} data-testid="audit-email-filter" />
          <button className="btn-secondary" onClick={load}>Refresh</button>
        </div>
      </div>
      {err && <div className="f-error">{err}</div>}
      <div className="card">
        <table className="tbl">
          <thead><tr><th>When</th><th>Action</th><th>Actor</th><th>Target</th><th>Summary</th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={5} style={{ padding: 14, textAlign: 'center', color: 'var(--text-faded)' }}>No matching events.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="audit-row" data-testid={`audit-row-${r.id}`}>
                <td>{new Date(r.created_at).toLocaleString()}</td>
                <td><code style={{ fontSize: 10 }}>{r.action}</code></td>
                <td>{r.actor_email || '—'}<div className="audit-meta">{r.actor_role || ''}</div></td>
                <td>{r.target_type || '—'}<div className="audit-meta">{r.target_id ? r.target_id.slice(0, 8) : ''}</div></td>
                <td>{r.summary || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── ACCOUNT TAB ────────────────────────────────────────────────────────────────
const AccountTab = () => {
  const { user } = useAuth();
  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setMsg(''); setErr('');
    if (pwd.next !== pwd.confirm) { setErr('Passwords do not match'); setBusy(false); return; }
    try {
      await api.post('/auth/change-password', { current_password: pwd.current, new_password: pwd.next });
      setMsg('Password updated.'); setPwd({ current: '', next: '', confirm: '' });
    } catch (ex) { setErr(formatApiError(ex)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="account-tab">
      <div className="feature-card" style={{ maxWidth: 460 }}>
        <div className="ttl">{user?.name}</div>
        <div className="sub">{user?.email} · {user?.role}</div>

        <form onSubmit={submit} style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}><KeyRound size={12} /> Change password</div>
          <label className="f-label">Current</label><input className="f-input" type="password" required value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })} data-testid="acc-current" />
          <label className="f-label" style={{ marginTop: 8 }}>New</label><input className="f-input" type="password" required minLength={8} value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })} data-testid="acc-next" />
          <label className="f-label" style={{ marginTop: 8 }}>Confirm new</label><input className="f-input" type="password" required minLength={8} value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} data-testid="acc-confirm" />
          {msg && <div className="module-banner" style={{ marginTop: 8 }}>{msg}</div>}
          {err && <div className="f-error">{err}</div>}
          <button type="submit" className="btn-primary" disabled={busy} style={{ marginTop: 10 }} data-testid="acc-submit">{busy ? 'Saving…' : 'Update password'}</button>
        </form>
      </div>
    </div>
  );
};

export default Settings;
